import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { MDText } from 'i18n-react';
import { useLocale } from '@adsk/bim360-matrix-react-helpers/es/locale';
import useRfiNotifications from './helpers/hooks/useRfiNotifications';
import RFIUI from '@adsk/rfis-ui/es';
import CreateEntityForm from '@adsk/bim360-matrix-react-entity-linker-plugins/es/CreateEntityForm';
import { ENTITY_TYPES } from '@adsk/bim360-matrix-react-entity-linker';
import Button from '@adsk/bim360-matrix-react-button';
import Loader from '@adsk/bim360-matrix-react-loader';
import getDeepLinkUrl from './helpers/getDeepLinkUrl';
import { REGIONS } from '@adsk/bim360-matrix-react-helpers/es/consts';
import { RFI_ENV_TYPES } from './consts';
import {
  currentUserType,
  currentProjectEntitlementsType,
  roleType,
  companyType,
  oxygenUserType
} from './rfisProviderProptypes';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import './styling/_rfiCreateNewEntityForm.scss';

const CreateEntityFormLoader = () => (
  <CreateEntityForm>
    <CreateEntityForm.Content>
      <Loader />
    </CreateEntityForm.Content>
    <CreateEntityForm.Footer></CreateEntityForm.Footer>
  </CreateEntityForm>
);

const RfiCreateNewEntityForm = ({
  env,
  region,
  currentProjectEntitlements,
  currentUser,
  usersInProject,
  rolesInProject,
  companiesInProject,
  onClose,
  onCreateSuccess,
  origin,
  initialFields
}) => {
  const { T, locale } = useLocale(({ locale }) => new MDText(require(`./i18n/${locale}.json`)));
  const { showSuccessNotification, showErrorNotification } = useRfiNotifications();

  const [isSubmitting, setSubmitting] = useState(false);
  const handleSaveStart = () => setSubmitting(true);

  const issueCreateFormContainerRef = useRef(null);
  const handleSubmit = () => issueCreateFormContainerRef.current.saveIssue();

  const handleSaveSuccess = entity => {
    const deepLinkUrl = getDeepLinkUrl(env, region, entity.projectId, entity.id);
    onClose();
    setSubmitting(false);
    onCreateSuccess({ ...entity, entityType: ENTITY_TYPES.RFI });
    showSuccessNotification(entity, deepLinkUrl);
  };

  const handleSaveError = () => {
    onClose();
    setSubmitting(false);
    showErrorNotification();
  };

  return (
    <RFIUI.RFIsProvider
      env={env}
      region={region}
      locale={locale}
      currentProjectEntitlements={currentProjectEntitlements}
      currentUser={currentUser}
      users={usersInProject}
      companies={companiesInProject}
      roles={rolesInProject}
      viewType={'project'}
      Loader={<CreateEntityFormLoader />}
    >
      <CreateEntityForm>
        <CreateEntityForm.Content className="rfis-entitylinker-plugin rfis-ui-root-element">
          <RFIUI.CreateRfiFormForEntityLinker
            ref={issueCreateFormContainerRef}
            onSaveStart={handleSaveStart}
            onSaveSuccess={handleSaveSuccess}
            onSaveError={handleSaveError}
            isSubmitting={isSubmitting}
            origin={origin}
            initialFields={initialFields}
          />
        </CreateEntityForm.Content>
        <CreateEntityForm.Footer>
          <Button styleType={Button.StyleType.LINK_SECONDARY} onClick={onClose}>
            {T.translate('CANCEL')}
          </Button>
          <Button
            styleType={Button.StyleType.PRIMARY}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? T.translate('CREATING') : T.translate('CREATE')}
          </Button>
        </CreateEntityForm.Footer>
      </CreateEntityForm>
    </RFIUI.RFIsProvider>
  );
};

RfiCreateNewEntityForm.displayName = 'RfiCreateNewEntityForm';

RfiCreateNewEntityForm.propTypes = {
  // These props are coming from EntityLinker

  /**
   * Callback that's being called when closing the modal.
   */
  onClose: PropTypes.func.isRequired,
  /**
   * Callback that's being called when creating RFI successfully.
   */
  onCreateSuccess: PropTypes.func.isRequired,
  /**
   * The ENV - one of `RFI_ENV_TYPES` values
   */
  env: PropTypes.oneOf(objectValues(RFI_ENV_TYPES)).isRequired,
  /**
   * The region. One of `REGIONS` values.
   */
  region: PropTypes.oneOf(objectValues(REGIONS)).isRequired,

  // These props should be passed from EntityLinker through createFormProps

  /**
   * Current project entitlements. Should be in the shape of `@adsk/bim360-mc-interface/es/models/EntitlementProjectItem`
   */
  currentProjectEntitlements: currentProjectEntitlementsType.isRequired,
  /**
   * The current user
   */
  currentUser: currentUserType.isRequired,
  /**
   * Users in current project. Should be in the shape of `@adsk/bim360-mc-interface/es/models/User`
   */
  usersInProject: PropTypes.arrayOf(oxygenUserType).isRequired,
  /**
   * Roles in current project. Should be in the shape of `@adsk/bim360-mc-interface/es/models/Role`
   */
  rolesInProject: PropTypes.arrayOf(roleType).isRequired,
  /**
   * Companies in current project. Should be in the shape of `@adsk/bim360-mc-interface/es/models/Company`
   */
  companiesInProject: PropTypes.arrayOf(companyType).isRequired,
  /**
   * origin to be tracked in analytics.
   */
  origin: PropTypes.string,
  /**
   * Initial fields to pre populate the form
   */
  initialFields: PropTypes.shape({
    title: PropTypes.string,
    assignedTo: PropTypes.string,
    dueDate: PropTypes.instanceOf(Date),
    description: PropTypes.string,
    suggestedAnswer: PropTypes.string,
    locationDescription: PropTypes.string,
    priority: PropTypes.oneOf(['Low', 'Normal', 'High']),
    linkedDocument: PropTypes.shape({
      urn: PropTypes.string.isRequired,
      pushpinAttributes: PropTypes.shape({
        externalId: PropTypes.string.isRequired,
        objectId: PropTypes.number.isRequired,
        attributesVersion: PropTypes.number.isRequired,
        position: PropTypes.shape({
          x: PropTypes.number.isRequired,
          y: PropTypes.number.isRequired,
          z: PropTypes.number.isRequired
        }).isRequired
      }).isRequired
    }),
    currentSheet: PropTypes.shape({
      is3D: PropTypes.bool.isRequired,
      sheetGuid: PropTypes.string.isRequired,
      sheetName: PropTypes.string.isRequired
    }),
    attachmentsAttributes: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        urn: PropTypes.string.isRequired,
        urnType: PropTypes.oneOf(['dm', 'oss']).isRequired
      })
    )
  })
};
export default RfiCreateNewEntityForm;
